import { AsyncQueue, Broadcaster } from "./async";
import { CredentialBroker, endpointTransport } from "./auth";
import { HermesError, asHermesError } from "./errors";
import { EventType, ServerRequestMethod, methodNotFound, type GatewayEvent, type ServerRequest, type ServerRequestReply } from "./events";
import { arr, bool, obj, str, type Json } from "./json";
import { RPCChannel, type ChannelOutput } from "./rpc";
import type { GatewaySocket } from "./transport";

export type ConnectionState =
  | { kind: "idle" }
  | { kind: "connecting"; attempt: number }
  | { kind: "connected"; hello: ServerHello }
  | { kind: "reconnecting"; attempt: number; retryAt: number; reason: string }
  | { kind: "needsLogin"; message: string };

export interface ServerHello {
  replayEpoch?: string;
  heartbeat: boolean;
  skinName?: string;
  connectedAt: number;
  /** 从开始建连（含鉴权、隧道握手）到收到 gateway.ready 的耗时（毫秒） */
  connectDuration?: number;
}

export function isConnected(s: ConnectionState): boolean {
  return s.kind === "connected";
}

export function connectionLabel(s: ConnectionState): string {
  switch (s.kind) {
    case "idle": return "未连接";
    case "connecting": return "连接中…";
    case "connected": return "已连接";
    case "reconnecting": return s.attempt > 1 ? `重连中（第 ${s.attempt} 次）` : "重连中…";
    case "needsLogin": return "需要重新登录";
  }
}

/** base 0.3s，封顶 15s，全抖动（与 apps/shared/reconnect-backoff.ts 一致） */
export function backoffDelay(attempt: number): number {
  const exponent = Math.min(Math.max(0, attempt - 1), 32);
  const ceiling = Math.min(15, 0.3 * Math.pow(2, exponent));
  return Math.max(0.3, Math.random() * ceiling);
}

interface ReadyWaiter {
  resolve: (c: RPCChannel) => void;
  reject: (e: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * 与一个 Hermes 网关的长连接（对应 HermesKit 的 HermesClient）。
 * - 建连前向 CredentialBroker 要 WS 地址（门禁模式下即时铸造 ticket）
 * - 断线自动重连（指数退避 + 抖动）
 * - 重连后对关注中的会话 session.activate + session.events.since 补回错过的事件，按 seq 去重；
 *   无法补齐时发出 `timibot.resync`，由上层用 session.resume 重建
 */
export class HermesClient {
  readonly events = new Broadcaster<GatewayEvent>();
  readonly states = new Broadcaster<ConnectionState>(true);
  readonly serverRequests = new Broadcaster<ServerRequest>();

  state: ConnectionState = { kind: "idle" };
  private channel?: RPCChannel;
  private wantsConnection = false;
  private attempt = 0;
  private connectTimer?: ReturnType<typeof setTimeout>;
  private connecting = false;
  private readyWaiters = new Set<ReadyWaiter>();
  private connectStartedAt?: number;

  private trackedSessions = new Set<string>();
  private lastSeenSeq = new Map<string, number>();
  private replayEpoch?: string;

  /** `openSocket`：测试时注入；默认按地址选线路（中转隧道 / 体验模式） */
  constructor(
    readonly broker: CredentialBroker,
    private readonly openSocket: (path: string) => Promise<GatewaySocket> = (path) => endpointTransport(broker.endpoint).openSocket(path, 15),
  ) {
    this.states.yield(this.state);
  }

  // MARK: 生命周期

  /** 开始（或保持）连接。重复调用无副作用。 */
  connect(): void {
    this.wantsConnection = true;
    if (this.channel || this.connecting || this.connectTimer) return;
    if (this.state.kind === "needsLogin") this.attempt = 0;
    this.scheduleConnect(0);
  }

  /** 主动断开，不再自动重连 */
  disconnect(): void {
    this.wantsConnection = false;
    if (this.connectTimer) clearTimeout(this.connectTimer);
    this.connectTimer = undefined;
    const old = this.channel;
    this.channel = undefined;
    old?.close();
    this.failReadyWaiters(HermesError.notConnected());
    this.setState({ kind: "idle" });
  }

  /** 凭据变更（重新登录后）立即重连 */
  reconnectNow(): void {
    const old = this.channel;
    this.channel = undefined;
    old?.close();
    if (this.connectTimer) clearTimeout(this.connectTimer);
    this.connectTimer = undefined;
    this.attempt = 0;
    this.wantsConnection = true;
    this.scheduleConnect(0);
  }

  /** 回到前台：检查连接是否还活着（页面被系统挂起过时，WebSocket 可能已经悄悄断了） */
  probe(): void {
    this.channel?.probe();
  }

  track(sessionID: string): void { this.trackedSessions.add(sessionID); }

  untrack(sessionID: string): void {
    this.trackedSessions.delete(sessionID);
    this.lastSeenSeq.delete(sessionID);
  }

  // MARK: 调用

  /** 调用一个网关方法。连接中/重连中会最多等待 `waitForConnection` 秒。 */
  async call(method: string, params: Json = {}, opts: { timeout?: number; waitForConnection?: number; signal?: AbortSignal } = {}): Promise<Json> {
    const channel = await this.readyChannel(opts.waitForConnection ?? 15);
    return channel.call(method, params, opts.timeout ?? RPCChannel.defaultTimeout, opts.signal);
  }

  respond(request: ServerRequest, reply: ServerRequestReply): void {
    this.channel?.respond(request.id, reply);
  }

  // MARK: 建连

  private scheduleConnect(delaySeconds: number): void {
    if (this.connectTimer) clearTimeout(this.connectTimer);
    this.connectTimer = setTimeout(() => {
      this.connectTimer = undefined;
      void this.performConnect();
    }, delaySeconds * 1000);
  }

  private async performConnect(): Promise<void> {
    if (!this.wantsConnection || this.channel || this.connecting) return;
    this.connecting = true;
    this.attempt += 1;
    this.setState({ kind: "connecting", attempt: this.attempt });
    try {
      const started = Date.now();
      const path = await this.broker.webSocketPath();
      const socket = await this.openSocket(path);
      this.connectStartedAt = started;
      if (!this.wantsConnection) { socket.close(); return; }
      const queue = new AsyncQueue<ChannelOutput>();
      const channel = new RPCChannel(socket, (o) => queue.push(o));
      this.channel = channel;
      channel.start();
      // 通道输出串行处理：重放进行时，新到的实时帧在队列里排队，重放结束后再按水位过滤
      void (async () => {
        for (;;) {
          let output: ChannelOutput;
          try { output = await queue.next(); } catch { return; }
          await this.handle(output, channel);
          if (output.kind === "closed") { queue.close(HermesError.cancelled()); return; }
        }
      })();
    } catch (e) {
      this.handleConnectFailure(asHermesError(e));
    } finally {
      this.connecting = false;
    }
  }

  private handleConnectFailure(err: HermesError): void {
    if (err.needsLogin) { this.enterNeedsLogin(err.detail || err.message); return; }
    if (err.kind === "http" && (err.status === 403 || err.status === 401) && this.broker.credential?.type === "sessionToken") {
      this.enterNeedsLogin("Token 被网关拒绝，请检查后重新填写");
      return;
    }
    this.scheduleRetry(err.message || "连接失败");
  }

  private enterNeedsLogin(message: string): void {
    this.wantsConnection = false;
    this.failReadyWaiters(HermesError.authenticationRequired(message));
    this.setState({ kind: "needsLogin", message });
  }

  private scheduleRetry(reason: string): void {
    if (!this.wantsConnection) { this.setState({ kind: "idle" }); return; }
    const delay = backoffDelay(this.attempt);
    this.setState({ kind: "reconnecting", attempt: this.attempt, retryAt: Date.now() + delay * 1000, reason });
    this.scheduleConnect(delay);
  }

  // MARK: 通道输出

  private async handle(output: ChannelOutput, source: RPCChannel): Promise<void> {
    if (source !== this.channel) return;
    switch (output.kind) {
      case "event":
        if (output.event.type === EventType.gatewayReady) await this.handleReady(output.event, source);
        else this.dispatchIfNewer(output.event);
        return;
      case "request":
        this.handleServerRequest(output.request, source);
        return;
      case "closed": {
        this.channel = undefined;
        this.failReadyWaiters(HermesError.notConnected());
        const error = output.error;
        if (error.needsLogin) this.enterNeedsLogin(error.detail || error.message);
        else if (this.wantsConnection) this.scheduleRetry(error.message || "连接断开");
        else this.setState({ kind: "idle" });
        return;
      }
    }
  }

  private async handleReady(event: GatewayEvent, channel: RPCChannel): Promise<void> {
    const payload = obj(event.payload);
    const epoch = str(payload?.replay_epoch);
    if (epoch && this.replayEpoch && epoch !== this.replayEpoch) {
      // 后端重启：seq 编号已重置，所有实时会话都不存在了，交给上层 resume
      const sessions = new Set([...this.trackedSessions, ...this.lastSeenSeq.keys()]);
      this.lastSeenSeq.clear();
      this.trackedSessions.clear();
      for (const sid of sessions) this.emitResync(sid, "backend_restarted");
    }
    if (epoch) this.replayEpoch = epoch;
    this.attempt = 0;
    const now = Date.now();
    const hello: ServerHello = {
      replayEpoch: epoch,
      heartbeat: bool(payload?.heartbeat) ?? false,
      skinName: str(obj(payload?.skin)?.name),
      connectedAt: now,
      connectDuration: this.connectStartedAt !== undefined ? now - this.connectStartedAt : undefined,
    };
    this.connectStartedAt = undefined;
    // 只重绑“这次连上之前”就在关注的会话；之后新建/接入的会话本来就绑在这条连接上
    const toReattach = new Set([...this.trackedSessions, ...this.lastSeenSeq.keys()]);
    this.setState({ kind: "connected", hello });
    const waiters = [...this.readyWaiters];
    this.readyWaiters.clear();
    for (const w of waiters) { clearTimeout(w.timer); w.resolve(channel); }
    this.events.yield(event);

    // 告诉网关本客户端会回答服务端请求，否则 approval/clarify 会被直接判失败
    channel.fireAndForget("client.capabilities", { server_requests: true });
    await this.reattach(toReattach, channel);
  }

  /** 同一会话的事件 seq 单调递增，统一按水位去重：重放和实时帧重叠时不会重复派发 */
  private dispatchIfNewer(event: GatewayEvent): void {
    if (event.sessionID && event.seq !== undefined) {
      if (event.seq <= (this.lastSeenSeq.get(event.sessionID) ?? 0)) return;
      this.lastSeenSeq.set(event.sessionID, event.seq);
    }
    this.events.yield(event);
  }

  /** 重连后：activate 重新绑定会话 → events.since 补回断线期间的事件 */
  private async reattach(sessions: Set<string>, channel: RPCChannel): Promise<void> {
    for (const sid of sessions) {
      if (channel !== this.channel) break;
      try {
        await channel.call("session.activate", { session_id: sid, omit_messages: true }, 15);
        const result = obj(await channel.call("session.events.since", { session_id: sid, last_seen: this.lastSeenSeq.get(sid) ?? 0 }, 10));
        const epoch = str(result?.epoch);
        if (epoch && this.replayEpoch && epoch !== this.replayEpoch) { this.emitResync(sid, "epoch_changed"); continue; }
        if (bool(result?.truncated) === true) this.emitResync(sid, "truncated");
        for (const raw of arr(result?.events) ?? []) {
          const p = obj(raw);
          if (!p) continue;
          const type = str(p.type);
          if (!type) continue;
          const sessionID = str(p.session_id);
          this.dispatchIfNewer({
            type, sessionID: sessionID || undefined, seq: typeof p.seq === "number" ? p.seq : undefined,
            payload: (p.payload ?? null) as Json, replayed: true,
          });
        }
      } catch (e) {
        if (e instanceof HermesError && e.kind === "rpc") {
          // 会话已被回收：交给上层按存储 id 重新 resume
          this.trackedSessions.delete(sid);
          this.lastSeenSeq.delete(sid);
          this.emitResync(sid, "not_live");
        } else {
          return; // 连接又断了：保留关注状态，下次重连再试
        }
      }
    }
  }

  private emitResync(sessionID: string, reason: string): void {
    this.events.yield({ type: EventType.clientResync, sessionID, payload: { reason }, replayed: false });
  }

  private handleServerRequest(request: ServerRequest, channel: RPCChannel): void {
    // 网页端没法回答的桌面专属请求立刻回 -32601，免得 agent 干等到超时
    if (!ServerRequestMethod.supportedOnMobile.includes(request.method) || this.serverRequests.subscriberCount === 0) {
      channel.respond(request.id, methodNotFound);
      return;
    }
    this.serverRequests.yield(request);
  }

  // MARK: 等待连接

  private readyChannel(waitSeconds: number): Promise<RPCChannel> {
    if (this.state.kind === "connected" && this.channel) return Promise.resolve(this.channel);
    if (!this.wantsConnection || waitSeconds <= 0) {
      if (this.state.kind === "needsLogin") return Promise.reject(HermesError.authenticationRequired(this.state.message));
      return Promise.reject(HermesError.notConnected());
    }
    return new Promise((resolve, reject) => {
      const waiter: ReadyWaiter = {
        resolve, reject,
        timer: setTimeout(() => { this.readyWaiters.delete(waiter); reject(HermesError.notConnected()); }, waitSeconds * 1000),
      };
      this.readyWaiters.add(waiter);
    });
  }

  private failReadyWaiters(error: unknown): void {
    const waiters = [...this.readyWaiters];
    this.readyWaiters.clear();
    for (const w of waiters) { clearTimeout(w.timer); w.reject(error); }
  }

  private setState(next: ConnectionState): void {
    if (JSON.stringify(next) === JSON.stringify(this.state)) return;
    this.state = next;
    this.states.yield(next);
  }
}
